import { useEffect, useState } from "react";
import { Upload, Trash2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ts, ediName } from "@/lib/api";
import { EdiTag, EDI_HELP } from "@/components/EdiTag";
import { PageHeader, Panel, Th, Td, Empty, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { EdiImportDialog } from "@/components/EdiImportDialog";
import { RawEdiDialog } from "@/components/RawEdiDialog";
import { ClearLogDialog } from "@/components/ClearLogDialog";
import { As2TestSendDialog } from "@/components/As2TestSendDialog";

const Chips = ({ value, onChange, options, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {options.map(([v, l]) => (
      <button key={v || "all"} onClick={() => onChange(v)} data-testid={`${testId}-${v || "all"}`} title={EDI_HELP[v] || ""}
        className={`rounded-sm border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${value === v ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-800 text-slate-400 hover:text-white"}`}>
        {l}
      </button>
    ))}
  </div>
);

export default function EdiLog() {
  const [rows, setRows] = useState([]);
  const [doc, setDoc] = useState("");
  const [dir, setDir] = useState("");
  const [imp, setImp] = useState(false);
  const [view, setView] = useState(null);
  const [clear, setClear] = useState(false);
  const [testSend, setTestSend] = useState(false);
  const load = () => api.get("/edi/messages", { params: { doc_type: doc || undefined, direction: dir || undefined } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, [doc, dir]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <PageHeader title="EDI message log" subtitle="Every inbound and outbound X12 transaction with Group O. Outbound transmission via VAN is MOCKED until VAN credentials are configured.">
        <Button variant="ghost" onClick={() => setClear(true)} className={btn.ghost} data-testid="edilog-clear-btn"><Trash2 className="mr-1.5 h-4 w-4" /> Clear log</Button>
        <Button variant="ghost" onClick={() => setTestSend(true)} className={btn.ghost} data-testid="edilog-test-send-btn"><Send className="mr-1.5 h-4 w-4" /> Send test file to Group O</Button>
        <Button onClick={() => setImp(true)} className={btn.primary} data-testid="edilog-import-btn"><Upload className="mr-1.5 h-4 w-4" /> Import EDI</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-4">
        <Chips value={dir} onChange={setDir} options={[["", "All"], ["in", "Inbound"], ["out", "Outbound"]]} testId="edilog-dir" />
        <Chips value={doc} onChange={setDoc} options={[["", "All types"], ...["850", "940", "945", "861", "856", "997"].map((c) => [c, ediName(c)])]} testId="edilog-doc" />
      </div>
      <Panel>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="edilog-table">
              <thead className="border-b border-slate-800"><tr><Th>When</Th><Th>Dir</Th><Th>Type</Th><Th>Control</Th><Th>Summary</Th><Th>Status</Th><Th>997 ack</Th><Th>Detail</Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {rows.map((m) => (
                  <tr key={m.id} onClick={() => setView(m.id)} className="cursor-pointer hover:bg-slate-800/40" data-testid={`edilog-row-${m.id}`}>
                    <Td className="whitespace-nowrap font-mono text-slate-500">{ts(m.created_at)}</Td>
                    <Td className="font-mono">{m.direction === "out" ? "OUT →" : "← IN"}</Td>
                    <Td className="whitespace-nowrap font-mono text-amber-300"><EdiTag code={m.doc_type} /></Td>
                    <Td className="font-mono">{m.control || "—"}</Td>
                    <Td className="text-slate-200">{m.summary}</Td>
                    <Td><StatusBadge status={m.status} /></Td>
                    <Td data-testid={`edilog-ack-${m.id}`}>{m.direction === "out" && m.doc_type !== "997" ? <StatusBadge status={m.ack_status || "awaiting_997"} /> : ""}</Td>
                    <Td className="max-w-[260px] truncate text-slate-400">{m.error}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty testId="edilog-empty">No messages.</Empty>}
      </Panel>
      <EdiImportDialog open={imp} onOpenChange={setImp} onDone={load} />
      <ClearLogDialog open={clear} onOpenChange={setClear} onDone={load} />
      <As2TestSendDialog open={testSend} onOpenChange={setTestSend} onDone={load} />
      <RawEdiDialog open={!!view} messageId={view} onOpenChange={(v) => !v && setView(null)} onChanged={load} />
    </div>
  );
}
